import { FC, useState } from "react";
import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, Restore } from "@mui/icons-material";
import { useNotify, useRecordContext, useRefresh } from "react-admin";
import { AxiosError, TProduct, updateProductV2 } from "@/api";
import { useAdmin } from "@/hooks/admin";
import { country } from "@/config/country";

const formatMoney = (n: number) =>
  new Intl.NumberFormat(country.locale ?? "es-CO", {
    style: "currency",
    currency: country.currency ?? "COP",
    maximumFractionDigits: 0,
  }).format(n || 0);

/**
 * InlineProductPriceField - editable price field that sits inside the
 * identity grid on the product Show. On save it PUTs the whole product
 * with the patched key (matches dataProvider's updateProductV2).
 */
export const InlineProductPriceField: FC<{
  label: string;
  source: "wholesalePrice" | "purchasePrice";
}> = ({ label, source }) => {
  const product = useRecordContext<TProduct>();
  const { hasPermission } = useAdmin();
  const notify = useNotify();
  const refresh = useRefresh();
  const initial = Number(((product as any)?.[source] as number) ?? 0);
  const [value, setValue] = useState<string>(String(initial));
  const [saving, setSaving] = useState(false);

  if (!product) return null;

  const canEdit = hasPermission("product.update");
  const numeric = Number(value);
  const dirty = !Number.isNaN(numeric) && numeric !== initial;

  if (!canEdit) {
    return (
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatMoney(initial)}
        </Typography>
      </Stack>
    );
  }

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await updateProductV2(product.id, {
        [source]: numeric,
        supplierId: (product as any).supplierId,
      } as Partial<TProduct>);
      notify("Precio actualizado", { type: "success" });
      refresh();
    } catch (e) {
      const msg =
        e instanceof AxiosError ? e.response?.data?.message : String(e);
      notify(msg ?? "Error al actualizar el precio", { type: "error" });
      setValue(String(initial));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      disabled={saving}
      inputProps={{ min: 0, step: 1 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography variant="body2" color="text.secondary">
              $
            </Typography>
          </InputAdornment>
        ),
        endAdornment: dirty ? (
          <InputAdornment position="end">
            <Stack direction="row" spacing={0.25}>
              <Tooltip title="Descartar">
                <IconButton
                  size="small"
                  onClick={() => setValue(String(initial))}
                  disabled={saving}
                >
                  <Restore fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Guardar">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={save}
                    disabled={saving}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </InputAdornment>
        ) : null,
      }}
      fullWidth
    />
  );
};
